const DESKTOP = '(min-width: 1024px)';

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const setup = (runway: HTMLElement): void => {
  const tabs = Array.from(runway.querySelectorAll<HTMLButtonElement>('[data-scroll-tab]'));
  const panels = Array.from(runway.querySelectorAll<HTMLElement>('[data-scroll-panel]'));
  const fills = Array.from(runway.querySelectorAll<HTMLElement>('[data-scroll-fill]'));
  const count = Math.min(tabs.length, panels.length);

  if (count < 2) {
    return;
  }

  const desktop = window.matchMedia(DESKTOP);

  let active = 0;
  let queued = false;

  const show = (index: number): void => {
    if (index === active) {
      return;
    }

    active = index;

    tabs.forEach((tab, current) => {
      const on = current === index;

      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
    });

    panels.forEach((panel, current) => {
      panel.classList.toggle('is-active', current === index);
    });
  };

  const paint = (index: number, local: number): void => {
    fills.forEach((fill, current) => {
      fill.style.width = current === index ? `${(local * 100).toFixed(2)}%` : '0%';
    });
  };

  const measure = (): void => {
    queued = false;

    if (!desktop.matches) {
      return;
    }

    const box = runway.getBoundingClientRect();
    const travel = box.height - window.innerHeight;

    if (travel <= 0) {
      return;
    }

    const progress = clamp(-box.top / travel, 0, 1) * count;
    const index = Math.min(Math.floor(progress), count - 1);

    show(index);
    paint(index, clamp(progress - index, 0, 1));
  };

  const request = (): void => {
    if (queued) {
      return;
    }

    queued = true;
    window.requestAnimationFrame(measure);
  };

  const scrollTo = (index: number): void => {
    const box = runway.getBoundingClientRect();
    const travel = box.height - window.innerHeight;
    const top = window.scrollY + box.top + (travel * index) / count;

    window.scrollTo({ top: top + 8, behavior: 'smooth' });
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => {
      if (desktop.matches) {
        scrollTo(index);
        return;
      }

      panels[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    tab.addEventListener('keydown', (event) => {
      const keys: Record<string, number> = {
        ArrowRight: index + 1,
        ArrowLeft: index - 1,
        ArrowDown: index + 1,
        ArrowUp: index - 1,
        Home: 0,
        End: count - 1,
      };

      const next = keys[event.key];

      if (undefined === next) {
        return;
      }

      event.preventDefault();

      const target = tabs[(next + count) % count];

      target.focus();
      target.click();
    });
  });

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request);

  desktop.addEventListener('change', () => {
    if (!desktop.matches) {
      show(0);
      paint(0, 0);
      return;
    }

    request();
  });

  measure();
};

const init = (): void => {
  const roots = document.querySelectorAll<HTMLElement>('[data-scroll-tabs]');

  if (!roots.length) {
    return;
  }

  document.documentElement.classList.add('has-scroll-tabs-js');

  roots.forEach(setup);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
